import { Avatar, UserActionsWrapper } from 'components/navigation/header/styled';
import { IUserProfileFields, userProfileFieldsSelector } from 'components/navigation/profileFields';
import { ShowAt, useScreenBreakpoint } from 'consts/breakpoints';
import Downshift from 'downshift';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { State } from 'store/state';
import { ActionWrapper, Counter, Divider, Email, MenuWrapper, MobileMenuWrapper, Name, UserEmailName, UserInfo } from './styled';
import { useLocale } from 'locale';
import ProfileIcon from 'assets/svg/investor-user.svg';
import WalletIcon from 'assets/svg/investor-wallet.svg';
import StarIcon from 'assets/svg/investor-save_neighborhood.svg';
// import FavoriteIcon from 'assets/svg/investor-saved.svg';
// import RingIcon from 'assets/svg/investor-ring.svg';
import ExitIcon from 'assets/svg/investor-exit.svg';
import { OverlayWrapper } from 'components/overlay-wrapper';
import HamburgerIcon from 'assets/svg/navigation/hamburger.svg';
import CloseIcon from 'assets/svg/close-modal.svg';
import { investorFavoriteNeighbourhoodsCountSelector } from 'store/state/domainData/selectors/investors';
import { userFavoritesCounterSelector } from 'store/state/domainData/selectors/favorites';
import { savedSearchesListCountSelector } from 'store/state/domainData/selectors';
import { navigateTo } from 'store/state/router/actions';
import { NavigateToFn, Route } from 'config/routes';
import { ProfileTab } from 'components/profile/Profile';
import { ROUTES_WITH_DISCLAIMER_BAR } from 'screens/Investors/MainLayout';
import { routeNameSelector } from 'store/state/selectors/router';


interface IUserMenuProps {
  userProfile: IUserProfileFields;
  onLogout: () => void;
  favoriteNeighbourhoodCount: number;
  savedListingsCount: number;
  savedSearchCount: number;
  navigate: NavigateToFn;
  routeName: Route;
}

const UserMenuBase: React.FC<IUserMenuProps> = ({ userProfile, onLogout, favoriteNeighbourhoodCount, savedListingsCount, savedSearchCount, routeName, navigate }) => {
  const { t } = useLocale();
  const screenBreakpoint = useScreenBreakpoint();
  const [ isMenuOpen, setIsMenuOpen ] = useState(false);
  const isMiddleScreen = screenBreakpoint === 3;
  const isMobile = screenBreakpoint === 1;
  const { initials, firstName, lastName, email, avatar } = userProfile;
  const withDisclaimer = ROUTES_WITH_DISCLAIMER_BAR.has(routeName);

  const menuConfig = [
    {
      label: 'investors.userMenu.profile',
      icon: <ProfileIcon />,
      route: {
        name: Route.InvestorsProfilePage,
        params: {},
      },
    },
    {
      label: 'investors.userMenu.preferences',
      icon: <WalletIcon />,
      route: {
        name: Route.InvestorsProfilePage,
        params: {
          initialTab: ProfileTab.Preferences,
        },
      },
    },
    {
      label: 'investors.userMenu.neighborhoods',
      icon: <StarIcon />,
      route: {
        name: Route.InvestorsFavoriteNeighourhoodPage,
        params: {},
      },
      count: favoriteNeighbourhoodCount,
    },
    // { label: 'investors.userMenu.favorite', icon: <FavoriteIcon />, count: savedListingsCount },
    // { label: 'investors.userMenu.savedSearches', icon: <RingIcon />, count: savedSearchCount },
  ];

  const handleClick = (route: { name: Route, params: any }) => () => {
    setIsMenuOpen(false);
    if (!route) return;
    if (route) navigate(route.name, route.params || {});
  };

  const handleDesktopBlur = () => {
    setIsMenuOpen(false);
  };

  const menuContent = (
    <>
      <UserInfo>
        {userProfile.initials ? (
          <Avatar
            data-auto="user-menu"
            userInitials={userProfile.initials}
            imageURL={userProfile.avatar}
            customSize={56}
            isBold
            textSize={17}
            withBorder
          />
        ) : null}
        <UserEmailName>
          {firstName || lastName ? (
            <Name>{lastName}{firstName ? ` ${firstName}` : ''}</Name>
          ) : null}
          {email ? <Email>{email}</Email> : null}
        </UserEmailName>
      </UserInfo>
      {menuConfig.map(el => (
        <ActionWrapper onClick={handleClick(el.route)} key={el.label}>
          {el.icon}
          {t(el.label as any)}
          {el.count ? <Counter>{el.count}</Counter> : null}
        </ActionWrapper>
      ))}
      <Divider />
      <ActionWrapper onClick={onLogout}>
        <ExitIcon />
        {t('investors.userMenu.logout')}
      </ActionWrapper>
    </>
  );

  return (
    <>
      <ShowAt at={1}>
        {isMenuOpen ? <CloseIcon width={24} height={24} onClick={() => setIsMenuOpen(false)} /> : <HamburgerIcon data-auto="more-button" onClick={() => setIsMenuOpen(true)} />}
        {isMenuOpen ? (
          <OverlayWrapper>
            <MobileMenuWrapper style={{ minHeight: window ? `calc(${window.innerHeight}px - 60px)` : 'auto', marginTop: withDisclaimer ? 122 : 60 }}>
              {menuContent}
            </MobileMenuWrapper>
          </OverlayWrapper>
        ) : null}
      </ShowAt>
      <ShowAt from={2}>
        <Downshift
          isOpen={isMenuOpen}
          onOuterClick={isMobile ? undefined : handleDesktopBlur}
        >
          {({ isOpen, getLabelProps }) => {
            const onAvatarClick = () => {
              setIsMenuOpen(!isMenuOpen);
            };
            return (
              <div>
                <UserActionsWrapper isMiddleScreen={isMiddleScreen}>
                  {initials ? (
                    <Avatar
                      data-auto="user-menu"
                      userInitials={initials}
                      imageURL={avatar}
                      {...getLabelProps()}
                      onClick={onAvatarClick}
                      customSize={40}
                      isBold
                    />
                  ) : null}
                  {isOpen ? (
                    <MenuWrapper>{menuContent}</MenuWrapper>
                  ) : null}
                </UserActionsWrapper>
              </div>
            );
          }}
        </Downshift>
      </ShowAt>
    </>
  );
};

const mapStateToProps = (state: State) => ({
  userProfile: userProfileFieldsSelector(state),
  favoriteNeighbourhoodCount: investorFavoriteNeighbourhoodsCountSelector(state),
  savedListingsCount: userFavoritesCounterSelector(state),
  savedSearchCount: savedSearchesListCountSelector(state),
  routeName: routeNameSelector(state),
});

const mapDispatchToProps = {
  navigate: navigateTo,
};

export const UserMenu = connect(mapStateToProps, mapDispatchToProps)(UserMenuBase);
